import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import useAppBarTitle from 'hooks/use-app-bar-title';
import MaterialTable from 'components/table';
import { routes } from 'types/routes';
import { Root } from './admin-activity-log-list.styled';
import { useRouter } from 'hooks/use-router';
import { ERoutesPrivate, ERoutesType } from 'types/routes';
import {
    ActionType,
    TAdminActivityLog,
    TGetAdminActivityLogsRequest,
    TGetAdminActivityLogsResponse,
} from 'types/entities/admin-activity-logs';
import useAdminActivityLogsGet from '../../hooks/use-admin-activity-log-get';
import adminActivityLogColumns from './admin-activity-log-list.columns';
import queryString from 'query-string';
import AdminActivityLogListFilters from '../../components/admin-activity-log-list-filters';
import TablePagination from 'components/table/table-pagination/table-pagination';
import { Col } from 'components/layout/layout';

export interface TAdminActivityLogListFilters
    extends TGetAdminActivityLogsRequest {
    page: number;
    limit: number;
}

const pageTitle = 'Admin Activity Logs';

const AdminActivityLogList = () => {
    useAppBarTitle(pageTitle);
    const { history, location } = useRouter();
    // TODO: extract the following to custom hook?
    const filtersFromUrlQuery = queryString.parse(
        location.search,
    ) as TAdminActivityLogListFilters;
    const defaultFilters = {
        adminEmail: '',
        nexId: '',
        page: 0,
        limit: 25,
        startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        actionType: ActionType.ALL,
    };
    const [filters, setFilters] = useState<TAdminActivityLogListFilters>({
        ...defaultFilters,
        ...filtersFromUrlQuery,
    });

    const [curData, setCurData] = useState<TGetAdminActivityLogsResponse>({
        data: [],
        count: 0,
        currentPage: 0,
        lastPage: 0,
    });

    const handlePageChange = (page: number) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setFilters((prev) => ({ ...prev, limit }));
    };

    const handleViewLogDetail = (rowData: TAdminActivityLog) => {
        history.push(
            `${
                routes[ERoutesType.Private][ERoutesPrivate.AdminActivityLog][0]
            }/${rowData.id}`,
        );
    };

    const getAppliedFilters = (filters: TGetAdminActivityLogsRequest) => {
        const newFilters = { ...filters };
        Object.keys(newFilters).forEach((key) => {
            if (key === 'actionType' && newFilters[key] === ActionType.ALL) {
                delete newFilters[key];
            }
            !newFilters[key] && delete newFilters[key];
        });
        return queryString.stringify(newFilters);
    };

    const { refetch, isLoading, isFetching } = useAdminActivityLogsGet(
        getAppliedFilters(filters),
        {
            onSuccess: (data) => {
                setCurData(data as TGetAdminActivityLogsResponse);
            },
        },
    );

    useEffect(() => {
        const queryString = getAppliedFilters(filters);
        history.replace({ pathname: location.pathname, search: queryString });
        refetch();
    }, [filters, history, location.pathname, refetch]);

    return (
        <>
            <Root>
                <AdminActivityLogListFilters
                    onFiltersChange={setFilters}
                    currentFilters={filters}
                    defaultFilters={defaultFilters}
                />
                <>
                    <Col justify={'end'}>
                        <TablePagination
                            page={curData.currentPage}
                            totalRows={curData.count}
                            setPage={handlePageChange}
                            setLimit={handleLimitChange}
                            rowsPerPage={filters.limit}
                        />
                    </Col>
                    <MaterialTable<TAdminActivityLog>
                        data={curData.data}
                        columns={adminActivityLogColumns()}
                        isLoading={isLoading || isFetching}
                        onClickRow={handleViewLogDetail}
                    />
                </>
            </Root>
        </>
    );
};

export default AdminActivityLogList;
